// src/context/AdminContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { getAdminData, ROLE_PERMISSIONS, ADMIN_ROLES } from '../services/adminService';

const AdminContext = createContext();

/**
 * Hook to use admin context
 * Must be used within AdminProvider
 */
export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within AdminProvider');
  }
  return context;
};

/**
 * Admin Context Provider
 * Manages admin-specific state and operations
 */
export const AdminProvider = ({ children }) => {
  const { user, isAdmin } = useAuth();
  const [adminSession, setAdminSession] = useState(false);
  const [adminRole, setAdminRole] = useState(null);
  const [adminPermissions, setAdminPermissions] = useState({});
  const [loadingAdminData, setLoadingAdminData] = useState(true);

  /**
   * Load admin data when user changes
   */
  useEffect(() => {
    const loadAdminData = async () => {
      if (isAdmin && user?.email) {
        setLoadingAdminData(true);
        try {
          const adminData = await getAdminData(user.email);
          if (adminData && adminData.status === 'active') {
            setAdminRole(adminData.role);
            setAdminPermissions(ROLE_PERMISSIONS[adminData.role] || {});
          } else {
            setAdminRole(null);
            setAdminPermissions({});
          }
        } catch (error) {
          console.error('[AdminContext] Error loading admin data:', error);
          setAdminRole(null);
          setAdminPermissions({});
        }
        setLoadingAdminData(false);
      } else {
        setAdminRole(null);
        setAdminPermissions({});
        setLoadingAdminData(false);
      }
    };

    loadAdminData();
  }, [isAdmin, user]);

  /**
   * Check if admin has specific permission
   */
  const hasPermission = (permission) => {
    return adminPermissions[permission] === true;
  };

  /**
   * Check if admin is super admin
   */
  const isSuperAdmin = () => {
    return adminRole === ADMIN_ROLES.SUPER_ADMIN;
  };

  /**
   * Start admin session
   * Used to track if admin has explicitly logged in to admin panel
   */
  const startAdminSession = () => {
    setAdminSession(true);
  };

  /**
   * End admin session
   */
  const endAdminSession = () => {
    setAdminSession(false);
  };

  const value = {
    isAdmin,
    adminSession,
    adminRole,
    adminPermissions,
    loadingAdminData,
    hasPermission,
    isSuperAdmin,
    startAdminSession,
    endAdminSession
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
};
